import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle, FileText, X, Plus } from "lucide-react";
import { useUpdateExpense } from "../hooks/useUpdateExpense";
import { useMarkReceiptsUploaded } from "../hooks/useMarkReceiptsUploaded";
import type { ExpenseItem } from "../hooks/useProviderExpenses";
import {
  updateExpenseSchema,
  getExpenseCategoryOptions,
  type UpdateExpenseInput,
  type Receipt,
} from "../utils/expense-schema";
import {
  uploadReservedReceipt,
  isValidReceiptFile,
  isValidReceiptSize,
  getFileTypeDisplay,
} from "../utils/receipt-utils";
import { format } from "date-fns";

const MAX_RECEIPTS = 10;

interface ExpenseEditDialogProps {
  expense: ExpenseItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExpenseEditDialog({
  expense,
  open,
  onOpenChange,
}: ExpenseEditDialogProps) {
  // Track existing receipts (from the expense) and new files to upload
  const [existingReceipts, setExistingReceipts] = useState<Receipt[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [uploadingReceipts, setUploadingReceipts] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: updateExpense, isPending } = useUpdateExpense();
  const { mutateAsync: markReceiptsUploaded } = useMarkReceiptsUploaded();

  const categoryOptions = getExpenseCategoryOptions();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<Omit<UpdateExpenseInput, "expenseId">>({
    resolver: zodResolver(updateExpenseSchema.omit({ expenseId: true })),
  });

  const selectedCategory = watch("category");

  // Total count of receipts (existing + new)
  const totalReceiptCount = existingReceipts.length + newFiles.length;

  // Reset form when expense changes
  useEffect(() => {
    if (expense) {
      setValue("category", expense.category);
      setValue("amount", expense.amount);
      setValue("expenseDate", expense.expenseDate);
      setValue("providerNote", expense.providerNote || "");
      setExistingReceipts(expense.receipts || []);
      setNewFiles([]);
    }
  }, [expense, setValue]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      return;
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      // Check if we've hit the max
      if (totalReceiptCount + validFiles.length >= MAX_RECEIPTS) {
        errors.push(`Maximum ${MAX_RECEIPTS} receipts allowed`);
        break;
      }

      // Validate file type
      if (!isValidReceiptFile(file)) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }

      // Validate file size
      if (!isValidReceiptSize(file)) {
        errors.push(`${file.name}: File size must be less than 5MB`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setNewFiles((prev) => [...prev, ...validFiles]);
    }

    if (errors.length > 0) {
      setUploadError(errors.join(". "));
    } else {
      setUploadError(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveExistingReceipt = (index: number) => {
    setExistingReceipts((prev) => prev.filter((_, i) => i !== index));
    setUploadError(null);
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadError(null);
  };

  const onSubmit = async (data: Omit<UpdateExpenseInput, "expenseId">) => {
    if (!expense) {
      return;
    }

    // Ensure at least one receipt will remain
    if (existingReceipts.length === 0 && newFiles.length === 0) {
      setUploadError("At least one receipt is required");
      return;
    }

    try {
      setUploadingReceipts(true);
      setUploadError(null);

      // Step 1: Update expense and get new receipt IDs/paths
      const result = await updateExpense({
        expenseId: expense.expenseId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        category: data.category as any, // Values match backend enum
        amount: data.amount,
        expenseDate: data.expenseDate,
        newReceipts:
          newFiles.length > 0
            ? newFiles.map((file) => ({ fileName: file.name }))
            : undefined,
        providerNote: data.providerNote,
      });

      // Step 2: Upload new files to the paths returned by the API
      if (result.newReceipts && result.newReceipts.length > 0) {
        for (let i = 0; i < result.newReceipts.length; i++) {
          const receipt = result.newReceipts[i];
          const file = newFiles[i];
          await uploadReservedReceipt({ file, receipt });
        }

        // Step 3: Mark new receipts as uploaded
        await markReceiptsUploaded({
          receiptIds: result.newReceipts.map((r) => r.id),
        });
      }

      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update expense:", error);
      // No client-side storage cleanup: providers cannot delete receipt
      // objects, and anything already uploaded is still referenced by its
      // receipt row, so it is removed with the expense on the server.
      setUploadError(
        error instanceof Error ? error.message : "Failed to update expense",
      );
    } finally {
      setUploadingReceipts(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      reset();
      setExistingReceipts([]);
      setNewFiles([]);
      setUploadError(null);
    }
    onOpenChange(newOpen);
  };

  if (!expense) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl dialog-safe-height overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit & Resubmit Expense</DialogTitle>
          <DialogDescription>
            Update your rejected expense and resubmit for approval.
          </DialogDescription>
        </DialogHeader>

        {/* Show rejection info */}
        {expense.latestRejection && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-medium mb-1">
                Rejected on{" "}
                {format(
                  new Date(expense.latestRejection.rejectedAt),
                  "MMM d, yyyy",
                )}
              </div>
              {expense.latestRejection.rejectionNote && (
                <div>{expense.latestRejection.rejectionNote}</div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Job Assignment (Read-only) */}
          <div className="space-y-2">
            <Label>Job Assignment</Label>
            <div className="p-3 bg-slate-50 rounded-md border">
              <div className="text-sm font-medium text-slate-700">
                {expense.jobOfferTitle || expense.facilityName}
              </div>
              {expense.facilityCity && expense.facilityState && (
                <div className="text-xs text-slate-500">
                  {expense.facilityCity}, {expense.facilityState}
                </div>
              )}
            </div>
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label htmlFor="category">
              Expense Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedCategory}
              onValueChange={(value) =>
                setValue("category", value as UpdateExpenseInput["category"])
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select expense category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-sm text-red-600">{errors.category.message}</p>
            )}
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount (USD) <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                $
              </span>
              <Input
                id="amount"
                type="text"
                placeholder="0.00"
                className="pl-7"
                {...register("amount")}
              />
            </div>
            {errors.amount && (
              <p className="text-sm text-red-600">{errors.amount.message}</p>
            )}
          </div>

          {/* Expense Date */}
          <div className="space-y-2">
            <Label htmlFor="expenseDate">
              Expense Date <span className="text-red-500">*</span>
            </Label>
            <Input id="expenseDate" type="date" {...register("expenseDate")} />
            {errors.expenseDate && (
              <p className="text-sm text-red-600">
                {errors.expenseDate.message}
              </p>
            )}
          </div>

          {/* Receipt Upload */}
          <div className="space-y-2">
            <Label htmlFor="receipt">
              Receipts
              <span className="text-xs text-slate-500 ml-2">
                ({totalReceiptCount}/{MAX_RECEIPTS} files)
              </span>
            </Label>
            <div className="space-y-2">
              {/* Existing receipts */}
              {existingReceipts.length > 0 && (
                <div className="space-y-2">
                  {existingReceipts.map((receipt, index) => (
                    <div
                      key={`existing-${receipt.path}-${index}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-md border"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <div className="text-sm font-medium text-slate-700">
                            {receipt.fileName}
                          </div>
                          <div className="text-xs text-slate-500">
                            Current receipt
                          </div>
                        </div>
                      </div>
                      {totalReceiptCount > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveExistingReceipt(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* New files to upload */}
              {newFiles.length > 0 && (
                <div className="space-y-2">
                  {newFiles.map((file, index) => (
                    <div
                      key={`new-${file.name}-${index}`}
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-400" />
                        <div>
                          <div className="text-sm font-medium text-slate-700">
                            {file.name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {getFileTypeDisplay(file.name)} •{" "}
                            {(file.size / 1024).toFixed(0)} KB • New
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveNewFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload area */}
              {totalReceiptCount < MAX_RECEIPTS && (
                <div className="border-2 border-dashed border-slate-300 rounded-md p-4 text-center hover:border-slate-400 transition-colors">
                  <input
                    ref={fileInputRef}
                    id="receipt"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label
                    htmlFor="receipt"
                    className="cursor-pointer flex flex-col items-center gap-1"
                  >
                    <Plus className="h-6 w-6 text-slate-400" />
                    <div className="text-sm text-slate-600">
                      Add{" "}
                      {existingReceipts.length > 0 || newFiles.length > 0
                        ? "more "
                        : ""}
                      receipts
                    </div>
                    <div className="text-xs text-slate-500">
                      PDF, JPG, PNG, or HEIC (max 5MB each)
                    </div>
                  </label>
                </div>
              )}
            </div>
            {uploadError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{uploadError}</AlertDescription>
              </Alert>
            )}
          </div>

          {/* Provider Note (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="providerNote">Notes (Optional)</Label>
            <Textarea
              id="providerNote"
              placeholder="Add any additional details about this expense..."
              rows={3}
              {...register("providerNote")}
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending || uploadingReceipts}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isPending || uploadingReceipts || totalReceiptCount === 0
              }
              className="flex-1 sm:flex-none"
            >
              {isPending || uploadingReceipts ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {uploadingReceipts ? "Uploading..." : "Resubmitting..."}
                </>
              ) : (
                "Resubmit Expense"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
